import IconBackgroundSvg from '@/assets/logos/icon-background-svg/icon-background.svg';
import { getBillingPlansPricingUrl } from '@/components/constants/billing-urls';
import { useIsHeidi2PricingPlansModalEnabled } from '@/features/flags';
import { IS_HIDE_PRO_MONTHLY_OPTION } from '@/features/flags/legacy-flags';
import { cn } from '@/lib/cn';
import { useTranslations, type TranslationKeys } from '@/utils/translations';
import { Period } from '@heidi/commerce/contracts';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { Link } from '@heidi/design-system/link';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import {
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import { TypographyH2 } from '@heidi/design-system/typography';
import { ExternalLink } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { PricingPlanCards } from './PricingPlanCards';
import { useTrialEndPlanSelectionModal } from './useTrialEndPlanSelectionModal';

export const TrialEndPlanSelectionModal = () => {
  const [period, setPeriod] = useState<Period>(Period.ONE_YEAR);

  const { handleOpenChange, handleViewAllPlans, shouldBeOpen, visiblePlans } =
    useTrialEndPlanSelectionModal(period);

  const { t, locale } = useTranslations();
  const isHeidi2PricingPlansModalEnabled = useIsHeidi2PricingPlansModalEnabled();
  return (
    <DialogDrawer open={shouldBeOpen} onOpenChange={handleOpenChange}>
      <DialogDrawerContent
        className={cn(
          'flex max-h-[90dvh] min-h-0 flex-col overflow-hidden bg-surface-accent',
          'w-full p-8 sm:max-w-none',
          visiblePlans.length === 2 && 'sm:max-w-2xl',
          visiblePlans.length === 3 && 'sm:max-w-5xl'
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pb-10 sm:pb-0">
          <div className="flex flex-col items-center gap-4 text-center">
            <Image src={IconBackgroundSvg} alt="Heidi" width={48} height={48} />
            <div className="flex flex-col gap-1">
              <TypographyH2 className="text-text-primary">
                {t('trialEndPlanSelectionModal.title' as TranslationKeys)}
              </TypographyH2>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {!IS_HIDE_PRO_MONTHLY_OPTION() && (
              <SegmentedControl
                value={period}
                onValueChange={(value) => setPeriod(value as Period)}
                className="items-center"
              >
                {/* bg-background-secondary needed to contrast against the bg-surface-accent dialog */}
                <SegmentedControlList className="bg-background-secondary">
                  <SegmentedControlTrigger value={Period.ONE_YEAR}>
                    {t('pricingPlansModal.annually')}
                    <Badge color="green" size="xs">
                      {t('pricingPlansModal.save30')}
                    </Badge>
                  </SegmentedControlTrigger>
                  <SegmentedControlTrigger value={Period.ONE_MONTH}>
                    {t('pricingPlansModal.monthly' as TranslationKeys)}
                  </SegmentedControlTrigger>
                </SegmentedControlList>
              </SegmentedControl>
            )}
            <PricingPlanCards period={period} />
          </div>
          <div className="flex justify-center">
            {isHeidi2PricingPlansModalEnabled ? (
              <Button variant="ghost" size="md" onClick={handleViewAllPlans}>
                {t('trialEndPlanSelectionModal.viewAllPlans')}
                <ExternalLink aria-hidden />
              </Button>
            ) : (
              <Button asChild variant="ghost" size="md">
                <Link href={getBillingPlansPricingUrl(locale)}>
                  {t('trialEndPlanSelectionModal.viewAllPlans')}
                  <ExternalLink aria-hidden />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
